import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { CmsPage, PageTemplate, Region, Suburb } from '../../../../models/content';
import { AccountService } from '../../../../services/account.service';
import { FileService } from '../../../../services/file.service';
import { ModalService } from '../../../../services/modal.service';
import {
  ReadinessDetail,
  SuburbReadinessService,
} from '../../../../services/suburb-readiness.service';
import { UserService } from '../../../../services/user.service';
import { faqOptionsFrom } from '../../faq-options';
import { accountSnapshot } from '../content/suburb-content';
import { applyPageTab, PageTabSave } from '../page/page-tab';
import defaults from '../template/page-template-defaults.json';
import { templateKeyFor } from '../template/suburb-template';
import { ContentRecordsService } from './content-records.service';

/**
 * What a suburb's Page tab (#262; page/content-suburb.component.ts) reads and writes: the template its page is built
 * from, what the site shows of each block on this page (the readiness detail), the save onto a freshly read record,
 * and converting a page designed before the template.
 */
@Injectable()
export class PageTabService {
  constructor(
    private records: ContentRecordsService,
    private readiness: SuburbReadinessService,
    private users: UserService,
    private account: AccountService,
    private files: FileService,
    private modal: ModalService
  ) {}

  regions(): Region[] {
    return this.records.regions();
  }

  /**
   * The template this record's page is built from: as stored, else the default the server and the site use until it
   * has been created (page-template-defaults.json, the server's fixture).
   */
  templateFor(record: Partial<Suburb>): PageTemplate {
    const key = templateKeyFor(record);
    return (
      this.records.template(key) ??
      ({ _meta: { id: `tpl-${key}`, index: 0 }, ...defaults[key] } as PageTemplate)
    );
  }

  /** The FAQ bank, for the page's FAQs. */
  faqOptions(): Array<{ label: string; value: string }> {
    return faqOptionsFrom(accountSnapshot(this.account)?.data?.content);
  }

  previewLink(fileId: number): Observable<string | null> {
    return this.files.getPreviewLink(String(fileId)).pipe(catchError(() => of(null)));
  }

  /** The hero photo upload (the same bucket and formats as a page hero's): the file, or null when cancelled. */
  uploadHero(): Observable<{ id: number; link: string | null } | null> {
    const ref = this.modal.fileUpload({
      title: 'Upload hero photo',
      bucketPrefix: 'page-images',
      // Not image/*: the website only optimises raster formats.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    return ref.onClose.pipe(
      take(1),
      map((r?: { files: Array<{ id: number | string; link?: string }> }) => {
        const file = r?.files?.[0];
        return file ? { id: +file.id, link: file.link ?? null } : null;
      })
    );
  }

  /** What the site shows of each block on this page, its FAQs and drafts; null for a record not saved yet or on error. */
  facts(record: Suburb, isNew: boolean): Observable<ReadinessDetail | null> {
    if (isNew) return of(null);
    return this.readiness.detail(record._meta.id).pipe(catchError(() => of(null)));
  }

  /** A page designed before the template that is still at the record's address (until it is converted). */
  designedPage(record: Suburb): CmsPage | undefined {
    return record.slug ? this.records.designedPage(record) : undefined;
  }

  /** Write the tab: a new record as it is, an existing one as the tab's changes to a freshly read copy. */
  save(isNew: boolean, s: PageTabSave): Observable<Suburb> {
    return isNew
      ? this.records.create(applyPageTab(s.original, s))
      : this.records.freshWrite(s.original, (fresh) => applyPageTab(fresh, s));
  }

  /** Converting a designed page is a Developer action (the server refuses anyone else). */
  get canConvert(): boolean {
    return this.users.userSourceValue?.isDeveloper() === true;
  }

  /** Convert, then re-read the account: the record gained its fields and blocks, and the page is gone. */
  convert(record: Suburb): Observable<unknown> {
    return this.readiness
      .convert([record._meta.id], false)
      .pipe(switchMap(() => this.account.get(true).pipe(take(1))));
  }
}
